import { http } from './client';
import type {
  IntegrationStatus,
  IntegrationTypeInfo,
} from './types';

export interface IntegrationTestResponse {
  status: IntegrationStatus;
  ok: boolean;
  error?: string;
}

export interface CreateInstanceInput {
  type: string;
  label: string;
  description?: string;
  config: Record<string, unknown>;
  seedTools?: boolean;
}

export const integrationsApi = {
  async list(): Promise<{ items: IntegrationStatus[] }> {
    const res = await http.get<{ items: IntegrationStatus[] }>('/v1/bot/integrations');
    return res.data;
  },

  async types(): Promise<{ types: IntegrationTypeInfo[] }> {
    const res = await http.get<{ types: IntegrationTypeInfo[] }>('/v1/bot/integrations/types');
    return res.data;
  },

  async get(id: string): Promise<IntegrationStatus> {
    const res = await http.get<IntegrationStatus>(
      `/v1/bot/integrations/${encodeURIComponent(id)}`,
    );
    return res.data;
  },

  async create(data: CreateInstanceInput): Promise<{ instance: IntegrationStatus }> {
    const res = await http.post<{ instance: IntegrationStatus }>(
      '/v1/bot/integrations',
      data,
    );
    return res.data;
  },

  async update(
    id: string,
    data: Partial<{ label: string; description: string; config: Record<string, unknown> }>,
  ): Promise<{ instance: IntegrationStatus }> {
    const res = await http.put<{ instance: IntegrationStatus }>(
      `/v1/bot/integrations/${encodeURIComponent(id)}`,
      data,
    );
    return res.data;
  },

  async setEnabled(id: string, enabled: boolean): Promise<{ instance: IntegrationStatus }> {
    const res = await http.patch<{ instance: IntegrationStatus }>(
      `/v1/bot/integrations/${encodeURIComponent(id)}/enabled`,
      { enabled },
    );
    return res.data;
  },

  async delete(id: string): Promise<{ deleted: true; instanceId: string; orphanedTools: string[] }> {
    const res = await http.delete<{ deleted: true; instanceId: string; orphanedTools: string[] }>(
      `/v1/bot/integrations/${encodeURIComponent(id)}`,
    );
    return res.data;
  },

  async test(id: string): Promise<IntegrationTestResponse> {
    const res = await http.post<IntegrationTestResponse>(
      `/v1/bot/integrations/${encodeURIComponent(id)}/test`,
      {},
    );
    return res.data;
  },
};
